import classNames from 'classnames';
import React from 'react';

import './ExchangeSummaryFundList.scss';

import {
  PRIOR_YEAR,
  PRODUCT_TYPE_LABEL_MAP,
  UNIT,
} from '../../../settings/settings';
import If from '../../Shared/If/If';
import {
  agreementTypeToProperCase,
  isProductType529,
} from '../../../utils/funds/funds-utility';
import {
  currency,
  formatShares,
  orEmpty,
  trainCase,
} from '../../../utils/utils';
import words from '../../../utils/words';
import { isAPP } from '../../../utils/advice/advice-utility';
import ToolTip from '../../Shared/ToolTip/ToolTip';
import { TOOLTIP_POSITION_BOTTOM } from '../../../utils/dom-utils';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import { TRADE_DATE_TOOLTIP } from '../../../utils/transactions-util';

const ExchangeSummaryFundList = ({
  amountComponent: AmountComponent,
  currentContributionYear,
  exchangeContributionYear,
  exchangeSummaryFundList,
  exchangeType,
  exchangeUnit,
  showIRAContributionYear = false,
  suppressDollarAmount = false,
  suppressExchangeAmount = false,
  priorContributionYear,
  exchangeTradeDates = {},
  showTradeDates,
}) => {
  const amountHeaderStyleNames = classNames(
    'exchange-summary-fund-list-exchange-amount',
    'exchange-summary-fund-list-header',
    {
      'exchange-summary-fund-list-suppress-amount':
        suppressExchangeAmount && !showIRAContributionYear,
      'exchange-summary-fund-list-remove-amount': showIRAContributionYear,
    }
  );

  const amountStyleNames = classNames(
    `exchange-summary-fund-list-exchange-amount `,
    {
      'exchange-summary-fund-list-exchange-amount-applied':
        exchangeUnit === UNIT.DOLLARS,
      'exchange-summary-fund-list-suppress-amount':
        suppressExchangeAmount && !showIRAContributionYear,
      'exchange-summary-fund-list-remove-amount': showIRAContributionYear,
    }
  );

  const dollarHeaderStyleNames = classNames(
    'exchange-summary-fund-list-exchange-dollars',
    'exchange-summary-fund-list-header',
    {
      'exchange-summary-fund-list-suppress-amount':
        suppressExchangeAmount || suppressDollarAmount,
    }
  );

  const dollarStyleNames = classNames(
    'exchange-summary-fund-list-exchange-dollars',
    'exchange-amount-applied',
    {
      'exchange-summary-fund-list-suppress-amount':
        suppressExchangeAmount || suppressDollarAmount,
    }
  );

  return (
    <div className="exchange-summary-fund-list-container">
      <div className="exchange-summary-fund-list-header exchange-summary-fund-list-row">
        <div className="exchange-summary-fund-list-full-name exchange-summary-fund-list-header">
          Account
        </div>
        <div className="exchange-summary-fund-list-shares exchange-summary-fund-list-header">
          {exchangeSummaryFundList.every((fund) => isAPP(fund.productType)) ? (
            ''
          ) : (
            <>
              Total {words.Shares}
              <sup>*</sup>
            </>
          )}
        </div>
        <div className="exchange-summary-fund-list-balances exchange-summary-fund-list-header">
          Total Balance<sup>*</sup>
        </div>
        <If test={showIRAContributionYear}>
          <div className="exchange-summary-fund-list-contribution-year exchange-summary-fund-list-header">
            Contribution year
          </div>
        </If>
        <div className={amountHeaderStyleNames}>Exchange Amount</div>
        <If test={exchangeUnit !== UNIT.DOLLARS}>
          <div className={dollarHeaderStyleNames}>
            Approximate Dollar Amount
          </div>
        </If>
      </div>
      {exchangeSummaryFundList.map((fund, index) => (
        <div
          className={`e2e-summary-fund-list-item-${index} exchange-summary-fund-list-row`}
          key={index}
        >
          <div className="exchange-summary-fund-list-full-name">
            <div className="e2e-classification-and-agreement-type exchange-summary-fund-list-full-name-line exchange-summary-fund-list-classification-and-agreement-type">
              <span>
                {isProductType529(fund.productType) ? (
                  <span className="blocked_data">
                    {fund.formattedEntityNames}
                  </span>
                ) : (
                  trainCase(fund.displayClassification)
                )}
                {' / '}
              </span>
              <span>
                {agreementTypeToProperCase(
                  isProductType529(fund.productType)
                    ? PRODUCT_TYPE_LABEL_MAP[fund.productType]
                    : fund.agreementType
                )}
              </span>
            </div>
            <div className="exchange-summary-fund-list-full-name-line">
              <span className="exchange-summary-fund-list-name">
                {fund.fundName}
              </span>
              <If
                test={
                  !isProductType529(fund.productType) &&
                  !isAPP(fund.productType)
                }
              >
                <span className="e2e-ticker exchange-summary-fund-list-ticker">
                  {fund.ticker}
                </span>
              </If>
            </div>
            <div className="blocked_data exchange-summary-fund-list-full-name-line exchange-summary-fund-list-account">
              <span className={classNames({ 'newly-added': fund.newFund })}>
                {fund.newFund ? 'Just Added' : orEmpty(fund.agreementNumber)}
              </span>
              {' • '}
              {fund.formattedEntityNames}
            </div>
            {showTradeDates && (
              <div className="exchange-projected-trade-date exchange-projected-trade-date-in-summary">
                Projected Trade Date:{' '}
                <span className="review-trade-place-date exchange-review-text-emphasis">
                  {fund.newFund
                    ? formatUnixDate(
                        exchangeTradeDates[
                          Object.keys(exchangeTradeDates).find((fundKey) =>
                            fundKey.includes(`newFund-${fund.trpCode}`)
                          )
                        ]
                      )
                    : formatUnixDate(exchangeTradeDates[fund.agreementID])}
                </span>
                <ToolTip
                  showBottomBorder={false}
                  message={TRADE_DATE_TOOLTIP}
                  placement={TOOLTIP_POSITION_BOTTOM}
                />
              </div>
            )}
          </div>
          <div className="exchange-summary-fund-list-shares">
            {!isAPP(fund.productType) && formatShares(fund.shares)}
          </div>
          <div className="exchange-summary-fund-list-balances">
            {currency(fund.balanceAsOf)}
          </div>
          <If test={showIRAContributionYear}>
            <div className="exchange-summary-fund-list-contribution-year">
              {
                // If no contribution year is specified (i.e-undefined), it is a current year contribution
                exchangeContributionYear === PRIOR_YEAR
                  ? priorContributionYear
                  : currentContributionYear
              }
            </div>
          </If>
          <div className={amountStyleNames}>
            <AmountComponent
              exchangeType={exchangeType}
              exchangeUnit={exchangeUnit}
              fund={fund}
            />
          </div>
          <If test={exchangeUnit !== UNIT.DOLLARS}>
            <div className={dollarStyleNames}>
              {currency(fund.grossAmountDollars)}
            </div>
          </If>
        </div>
      ))}
    </div>
  );
};

export default ExchangeSummaryFundList;
